import React, { useState } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { TrackPricing, AddOnService } from '../../hooks/useTrackData';
import { DateValidation } from '../../hooks/useBookingValidation';
import { formatCurrency } from '../../lib/currencyUtils';

interface FloatingBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPricing: TrackPricing | null;
  selectedDates: string[];
  setSelectedDates: (dates: string[]) => void;
  selectedStartTime: string;
  setSelectedStartTime: (time: string) => void;
  selectedEndTime: string;
  setSelectedEndTime: (time: string) => void;
  timeError: string;
  isMultiDateBooking: boolean;
  setIsMultiDateBooking: (value: boolean) => void;
  lightingFee: number;
  isPromotor: boolean;
  getDateValidation: (date: string) => DateValidation | undefined;
  formatDateForDisplay: (date: string) => string;
  handleStartTimeChange: (time: string) => void;
  addDate: () => void;
  removeDate: (index: number) => void;
  updateDate: (index: number, value: string) => void;
  isBigEvent: () => boolean;
  getDepositAmount: () => number;
  selectedAddOns: string[];
  addOns: AddOnService[];
  toggleAddOn: (addOnId: string) => void;
  showAddOnsModal: boolean;
  setShowAddOnsModal: (show: boolean) => void;
  paymentOption: 'full' | 'deposit';
  setPaymentOption: (option: 'full' | 'deposit') => void;
  calculateTotal: () => number;
  getPaymentAmount: () => number;
  hasInvalidDates: () => boolean;
  validatingDates: boolean;
  handleBooking: () => Promise<void>;
}

// Get minimum date (today) and maximum date (6 months from now)
const minDate = new Date().toISOString().split('T')[0];
const maxDate = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const FloatingBookingModal: React.FC<FloatingBookingModalProps> = ({
  isOpen,
  onClose,
  selectedPricing,
  selectedDates,
  setSelectedDates,
  selectedStartTime,
  setSelectedStartTime,
  selectedEndTime,
  setSelectedEndTime,
  timeError,
  isMultiDateBooking,
  setIsMultiDateBooking,
  lightingFee,
  isPromotor,
  getDateValidation,
  formatDateForDisplay,
  handleStartTimeChange,
  addDate,
  removeDate,
  updateDate,
  isBigEvent,
  getDepositAmount,
  selectedAddOns,
  addOns,
  toggleAddOn,
  showAddOnsModal,
  setShowAddOnsModal,
  paymentOption,
  setPaymentOption,
  calculateTotal,
  getPaymentAmount,
  hasInvalidDates,
  validatingDates,
  handleBooking
}) => {
  if (!isOpen || !selectedPricing) return null;

  const [isBooking, setIsBooking] = useState(false);

  const handleBookNow = async () => {
    try {
      setIsBooking(true);
      await handleBooking();
      // The modal will be closed by the parent component after successful booking
    } catch (error) {
      console.error("Booking error:", error);
      setIsBooking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-zinc-900 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">Complete Your Booking</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6 p-4 bg-zinc-800 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-2">{selectedPricing.title} - {formatCurrency(selectedPricing.price)}</h3>
          <p className="text-gray-400 text-sm">{selectedPricing.description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
              {selectedPricing.duration_hours} {selectedPricing.duration_hours === 1 ? 'Hour' : 'Hours'}
            </span>
            {selectedPricing.use_traction_compound && (
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Traction Compound
              </span>
            )}
            {selectedPricing.blocks_full_day && (
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                Full Day Event
              </span>
            )}
          </div>
        </div>

        {/* Date and Time Selection */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-6">Select Date and Time</h3>
          
          {/* Multi-date option for promotors */}
          {isPromotor && (
            <div className="mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isMultiDateBooking}
                  onChange={(e) => {
                    setIsMultiDateBooking(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedDates([selectedDates[0] || '']);
                    }
                  }}
                  className="mr-3"
                />
                <span className="text-white font-medium">Multi-date event booking</span>
              </label>
              <p className="text-gray-400 text-sm mt-1">
                Book multiple dates for your event series (promotor feature)
              </p>
              {isPromotor && (
                <div className="mt-2 p-3 bg-amber-900 border border-amber-700 rounded-lg">
                  <p className="text-amber-200 text-sm">
                    <strong>Promotor Notice:</strong> Promotor bookings require a 7-day buffer around major events. 
                    The system will automatically check for conflicts with big racing events.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Date Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {isMultiDateBooking ? 'Select Event Dates' : 'Select Date'}
            </label>
            
            {isMultiDateBooking ? (
              <div className="space-y-3">
                {selectedDates.map((date, index) => {
                  const validation = getDateValidation(date);
                  const hasError = validation && !validation.isValid && date.trim() !== '';
                  
                  return (
                    <div key={index}>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          min={minDate}
                          max={maxDate}
                          value={date}
                          onChange={(e) => updateDate(index, e.target.value)}
                          className={`flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg focus:ring-2 focus:outline-none ${
                            hasError 
                              ? 'border-2 border-primary-500 focus:ring-primary-500' 
                              : 'focus:ring-primary-500'
                          }`}
                        />
                        {selectedDates.length > 1 && (
                          <button
                            onClick={() => removeDate(index)}
                            className="text-gray-400 hover:text-primary-500"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                      {hasError && (
                        <p className="text-primary-400 text-sm mt-1">
                          {validation?.error}
                        </p>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={addDate}
                  className="text-primary-500 hover:text-primary-400 text-sm font-semibold flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Another Date
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={selectedDates[0]}
                  onChange={(e) => {
                    setSelectedDates([e.target.value]);
                    setSelectedStartTime('');
                    setSelectedEndTime('');
                  }}
                  className={`w-full bg-zinc-800 text-white px-4 py-2 rounded-lg focus:ring-2 focus:outline-none ${
                    getDateValidation(selectedDates[0])?.isValid === false && selectedDates[0].trim() !== ''
                      ? 'border-2 border-primary-500 focus:ring-primary-500'
                      : 'focus:ring-primary-500'
                  }`}
                />
                {getDateValidation(selectedDates[0])?.isValid === false && selectedDates[0].trim() !== '' && (
                  <p className="text-primary-400 text-sm mt-1">
                    {getDateValidation(selectedDates[0])?.error}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Custom Time Selection */}
          {(selectedDates[0] || (isMultiDateBooking && selectedDates.some(date => date.trim() !== ''))) && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Choose Your Times
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={selectedStartTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">End Time</label>
                  <input
                    type="time"
                    value={selectedEndTime}
                    onChange={(e) => setSelectedEndTime(e.target.value)}
                    className="w-full bg-zinc-800 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>
              </div>
              
              {/* Time Validation Error */}
              {timeError && (
                <div className="mt-2 p-3 bg-red-900 border border-red-700 rounded-lg">
                  <p className="text-red-200 text-sm">{timeError}</p>
                </div>
              )}
              
              {/* Time Selection Help */}
              <div className="mt-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg">
                <p className="text-gray-300 text-sm">
                  <strong>Package Duration:</strong> {selectedPricing.duration_hours} hour{selectedPricing.duration_hours !== 1 ? 's' : ''}
                  {selectedStartTime && selectedEndTime && !timeError && (
                    <span className="ml-2 text-green-300">✓ Time selection is valid</span>
                  )}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Start time will auto-calculate the end time, but you can adjust both as needed.
                </p>
                {isMultiDateBooking && (
                  <p className="text-gray-400 text-xs mt-1">
                    <strong>Multi-date booking:</strong> Same time will be applied to all selected dates.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Lighting Fee Notice - Only show for non-full-day events */}
          {lightingFee > 0 && !selectedPricing.blocks_full_day && (
            <div className="mb-6 p-4 bg-yellow-900 rounded-lg">
              <p className="text-yellow-200">
                <strong>Lighting Fee:</strong> An additional {formatCurrency(lightingFee)} lighting fee applies for bookings extending beyond 6:00 PM.
                {isMultiDateBooking && selectedDates.filter(d => d.trim() !== '').length > 1 && (
                  <span className="block mt-1">This fee will be applied to each date.</span>
                )}
              </p>
            </div>
          )}

          {/* Big Event Deposit Notice */}
          {isBigEvent() && (
            <div className="mb-6 p-4 bg-orange-900 border border-orange-700 rounded-lg">
              <p className="text-orange-200 text-sm font-medium">
                <strong>Security Deposit Required:</strong> This booking requires a security deposit of {formatCurrency(getDepositAmount())} in addition to the track fee.
              </p>
              <p className="text-orange-300 text-xs mt-1">
                The security deposit will be refunded if no damage occurs during your event.
              </p>
            </div>
          )}
        </div>

        {/* Add-ons Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Add-on Services</h3>
            <button
              onClick={() => setShowAddOnsModal(true)}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-1 rounded-lg flex items-center gap-1 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Services
            </button>
          </div>
          
          {selectedAddOns.length > 0 ? (
            <div className="space-y-2">
              {selectedAddOns.map(addOnId => {
                const addOn = addOns.find(a => a.id === addOnId);
                if (!addOn) return null;
                return (
                  <div key={addOn.id} className="flex justify-between items-center bg-zinc-800 p-3 rounded-lg">
                    <div>
                      <h4 className="font-semibold text-white text-sm">{addOn.name}</h4>
                      <p className="text-xs text-gray-400">{addOn.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-primary-500 text-sm">{formatCurrency(addOn.price)}</span>
                      <button
                        onClick={() => toggleAddOn(addOn.id)}
                        className="text-gray-400 hover:text-primary-500"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No add-on services selected</p>
          )}
        </div>

        {/* Payment Option Selection - Only show for regular bookings */}
        {selectedStartTime && selectedEndTime && !timeError && !hasInvalidDates() && !isBigEvent() && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Payment Option
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentOption('full')}
                className={`p-4 rounded-lg text-left transition-colors ${
                  paymentOption === 'full'
                    ? 'bg-primary-600 text-white'
                    : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                }`}
              >
                <div className="font-semibold mb-1">Pay Track Fee Only</div>
                <div className="text-sm opacity-80">
                  Pay {formatCurrency(calculateTotal())} now
                  {isMultiDateBooking && selectedDates.filter(d => d.trim() !== '').length > 1 && (
                    <span className="block">({selectedDates.filter(d => d.trim() !== '').length} dates)</span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setPaymentOption('deposit')}
                className={`p-4 rounded-lg text-left transition-colors ${
                  paymentOption === 'deposit'
                    ? 'bg-primary-600 text-white'
                    : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                }`}
              >
                <div className="font-semibold mb-1">Pay with Security Deposit</div>
                <div className="text-sm opacity-80">
                  Pay {formatCurrency(getPaymentAmount())} now ({formatCurrency(calculateTotal())} + {formatCurrency(getDepositAmount())} deposit)
                  {isMultiDateBooking && selectedDates.filter(d => d.trim() !== '').length > 1 && (
                    <span className="block">({selectedDates.filter(d => d.trim() !== '').length} dates)</span>
                  )}
                  <div className="text-xs mt-1 text-yellow-300">
                    Deposit refunded if no damage occurs
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Add-ons Modal */}
        {showAddOnsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[60]">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-2xl w-full">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Add-on Services</h2>
                <button
                  onClick={() => setShowAddOnsModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {addOns.map(addOn => (
                  <div key={addOn.id} className="flex justify-between items-center bg-zinc-800 p-4 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-white">{addOn.name}</h3>
                      <p className="text-sm text-gray-400">{addOn.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-primary-500">
                        {formatCurrency(addOn.price)}
                        {isMultiDateBooking && selectedDates.filter(d => d.trim() !== '').length > 1 && (
                          <span className="text-xs text-gray-400 block">per date</span>
                        )}
                      </span>
                      <button
                        onClick={() => toggleAddOn(addOn.id)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          selectedAddOns.includes(addOn.id)
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-zinc-700 text-white hover:bg-zinc-600'
                        }`}
                      >
                        {selectedAddOns.includes(addOn.id) ? (
                          <Minus className="h-5 w-5" />
                        ) : (
                          <Plus className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowAddOnsModal(false)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Summary and Submit Button */}
        <div className="mt-6 pt-6 border-t border-zinc-800">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-gray-400">
                {(isBigEvent() || paymentOption === 'deposit') ? 'Total Amount Due Now' : 'Total Amount'}
              </p>
              <p className="text-2xl font-bold text-white">{formatCurrency(getPaymentAmount())}</p>
              {(isBigEvent() || paymentOption === 'deposit') && (
                <div className="text-sm text-gray-400 mt-1">
                  <p>Track Fee: {formatCurrency(calculateTotal())}</p>
                  <p>Security Deposit: {formatCurrency(getDepositAmount())} (refundable)</p>
                </div>
              )}
              {isMultiDateBooking && selectedDates.filter(d => d.trim() !== '').length > 1 && (
                <p className="text-sm text-blue-400">
                  Total for {selectedDates.filter(d => d.trim() !== '').length} dates
                </p>
              )}
            </div>
            <button
              onClick={handleBookNow}
              disabled={
                !selectedDates.some(date => date.trim() !== '') || 
                !selectedStartTime || 
                !selectedEndTime || 
                !!timeError ||
                hasInvalidDates() ||
                validatingDates ||
                isBooking
              }
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBooking ? 'Processing...' : validatingDates ? 'Validating...' : 'Book Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingBookingModal;